import React from 'react';
import { Check, HelpCircle } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { Link } from 'react-router-dom';
export function Pricing() {
  const plans = [{
    name: 'Starter',
    price: '$999',
    period: '/month',
    description: 'Essential monitoring for small to medium networks.',
    features: ['Up to 100 network nodes', '7-day data retention', 'Basic threat detection', 'Email support', '1 user account', 'Standard API access'],
    cta: 'Contact Sales',
    variant: 'secondary' as const
  }, {
    name: 'Professional',
    price: '$1,499',
    period: '/month',
    description: 'Advanced analytics for growing enterprises.',
    features: ['Up to 1,000 network nodes', '30-day data retention', 'Advanced threat detection', 'Priority email & chat support', '5 user accounts', 'Full API access', 'Custom dashboards'],
    cta: 'Contact Sales',
    variant: 'primary' as const,
    popular: true
  }, {
    name: 'Enterprise',
    price: 'Custom',
    period: '',
    description: 'Full-scale platform for global infrastructure.',
    features: ['Unlimited network nodes', '365-day data retention', 'AI-driven threat mitigation', '24/7 dedicated phone support', 'Unlimited users', 'SSO & advanced security', 'On-premise deployment option', 'Dedicated success manager'],
    cta: 'Contact Sales',
    variant: 'outline' as const
  }];
  return <div className="min-h-screen bg-slate-950 py-20" data-id="element-292">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-293">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-294">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6" data-id="element-295">
            Simple, Transparent Pricing
          </h1>
          <p className="text-xl text-slate-400" data-id="element-296">
            Choose the plan that best fits your network scale and security
            needs.
          </p>
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-24" data-id="element-297">
          {plans.map(plan => <Card key={plan.name} className={`relative flex flex-col ${plan.popular ? 'border-cyan-500 ring-1 ring-cyan-500 shadow-2xl shadow-cyan-900/20' : ''}`} data-id="element-298">
              {plan.popular && <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2" data-id="element-299">
                  <span className="bg-cyan-500 text-slate-950 text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wide" data-id="element-300">
                    Most Popular
                  </span>
                </div>}

              <div className="mb-8" data-id="element-301">
                <h3 className="text-xl font-semibold text-white mb-2" data-id="element-302">
                  {plan.name}
                </h3>
                <div className="flex items-baseline mb-4" data-id="element-303">
                  <span className="text-4xl font-bold text-white" data-id="element-304">
                    {plan.price}
                  </span>
                  <span className="text-slate-500 ml-2" data-id="element-305">{plan.period}</span>
                </div>
                <p className="text-slate-400 text-sm" data-id="element-306">{plan.description}</p>
              </div>

              <ul className="space-y-4 mb-8 flex-grow" data-id="element-307">
                {plan.features.map((feature, idx) => <li key={idx} className="flex items-start" data-id="element-308">
                    <Check className="h-5 w-5 text-cyan-500 mr-3 shrink-0" data-id="element-309" />
                    <span className="text-slate-300 text-sm" data-id="element-310">{feature}</span>
                  </li>)}
              </ul>

              <Link to="/contact" data-id="element-311">
                <Button variant={plan.variant} className="w-full" data-id="element-312">
                  {plan.cta}
                </Button>
              </Link>
            </Card>)}
        </div>

        {/* FAQ Section */}
        <div className="max-w-3xl mx-auto border-t border-slate-800 pt-16" data-id="element-313">
          <h2 className="text-3xl font-bold text-white mb-12 text-center" data-id="element-314">
            Frequently Asked Questions
          </h2>
          <div className="space-y-8" data-id="element-315">
            {[{
            q: 'Can I deploy njgod on-premise?',
            a: 'Yes, our Enterprise plan supports on-premise deployment for organizations with strict data residency requirements. We provide comprehensive installation support, including dedicated technical resources to ensure seamless integration with your existing infrastructure. Our on-premise solution includes the same powerful features as our cloud offering, with additional customization options to meet your specific security and compliance needs.'
          }, {
            q: 'How is data retention handled?',
            a: 'Data retention varies by plan to accommodate different organizational needs and compliance requirements. The Starter plan includes 7 days of data retention, Professional offers 30 days, and Enterprise customers can retain data for up to 365 days. All data is encrypted at rest using AES-256 encryption and in transit using TLS 1.3. For organizations with specific regulatory requirements, we can configure custom retention policies and provide detailed audit logs for compliance reporting.'
          }, {
            q: 'Do you offer volume discounts?',
            a: "Yes, we offer substantial volume discounts for large-scale deployments and multi-year commitments. Our pricing model is designed to scale with your organization's growth. For deployments exceeding 10,000 network nodes or annual contracts, please contact our sales team at sales@njgod.com for a custom quote. We also offer special pricing for educational institutions, non-profit organizations, and government agencies."
          }, {
            q: 'How do I get started?',
            a: "Getting started with njgod is straightforward. Contact our sales team at sales@njgod.com to discuss your specific requirements and network architecture. We'll schedule a comprehensive demo tailored to your use case, followed by a technical consultation to design the optimal deployment strategy. Our onboarding process typically takes 2-4 weeks and includes dedicated support from our customer success team to ensure you're getting maximum value from day one."
          }, {
            q: 'What kind of support do you provide?',
            a: 'We offer tiered support based on your plan. Starter plan customers receive email support with 24-hour response times. Professional plan customers get priority email and chat support with 4-hour response times during business hours. Enterprise customers receive 24/7 dedicated phone support with a 1-hour response time SLA, plus a dedicated customer success manager and quarterly business reviews. All plans include access to our comprehensive documentation, video tutorials, and community forums.'
          }, {
            q: 'Is njgod compliant with industry regulations?',
            a: 'Yes, njgod maintains compliance with major industry standards and regulations including SOC 2 Type II, GDPR, HIPAA, ISO 27001, and PCI DSS. We undergo regular third-party audits and maintain detailed compliance documentation. For Enterprise customers, we can provide BAAs (Business Associate Agreements) for HIPAA compliance, DPAs (Data Processing Agreements) for GDPR, and custom compliance reports. Our security team stays current with evolving regulatory requirements to ensure your data remains protected and compliant.'
          }, {
            q: 'Can I integrate njgod with my existing tools?',
            a: 'Absolutely. njgod offers 50+ native integrations with popular DevOps, monitoring, and security tools including Slack, PagerDuty, Jira, Splunk, Datadog, and more. Our comprehensive REST API and webhooks enable custom integrations with proprietary systems. We also provide SDKs in Python, JavaScript, Go, and Java to accelerate integration development. Our Professional Services team is available to assist with complex integration projects and can develop custom connectors for specialized tools upon request.'
          }, {
            q: 'What happens if I exceed my plan limits?',
            a: "If you approach your plan's node limit, we'll proactively notify you with plenty of advance warning. You can seamlessly upgrade to a higher tier at any time, with pricing prorated for the remainder of your billing period. For temporary spikes in usage, we offer flexible burst capacity at reasonable overage rates. Our goal is to ensure your monitoring never goes dark due to capacity constraints. Enterprise customers can work with their account manager to establish custom capacity planning and automatic scaling policies."
          }].map((faq, idx) => <div key={idx} className="bg-slate-900/30 rounded-lg p-6 hover:bg-slate-900/50 transition-colors" data-id="element-316">
                <h3 className="text-lg font-semibold text-white mb-3 flex items-start" data-id="element-317">
                  <HelpCircle className="h-5 w-5 text-cyan-400 mr-3 mt-0.5 shrink-0" data-id="element-318" />
                  <span data-id="element-319">{faq.q}</span>
                </h3>
                <p className="text-slate-400 ml-8 leading-relaxed" data-id="element-320">{faq.a}</p>
              </div>)}
          </div>
        </div>
      </div>
    </div>;
}